type LocationFacetInput = {
  location?: unknown;
  count?: unknown;
};

type LocationDefinition = {
  id: string;
  label: string;
  isPreferredSource?: boolean;
};

export type NormalizedLocation = {
  id: string;
  label: string;
  source: string;
  isPreferredSource: boolean;
};

export type ExactLocationCard = NormalizedLocation & {
  /** The verified count for this single, exact-match source value. */
  count: number;
};

const locationDefinitions: Record<string, LocationDefinition> = {
  bengaluru: { id: "bengaluru", label: "Bengaluru", isPreferredSource: true },
  "bengaluru/bangalore": { id: "bengaluru", label: "Bengaluru" },
  gurgaon: { id: "gurugram", label: "Gurugram", isPreferredSource: true },
  delhi: { id: "delhi", label: "Delhi", isPreferredSource: true },
  "delhi/ncr": { id: "delhi-ncr", label: "Delhi NCR", isPreferredSource: true },
  mumbai: { id: "mumbai", label: "Mumbai", isPreferredSource: true },
  "mumbai (all areas)": { id: "mumbai", label: "Mumbai" },
  "mumbai suburbs": { id: "mumbai", label: "Mumbai" },
  "ernakulam / kochi / cochin": { id: "kochi", label: "Kochi", isPreferredSource: true },
  "wfh/remote": { id: "remote", label: "Remote", isPreferredSource: true },
};

function normalizedKey(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function countFrom(value: unknown): number {
  const numeric =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : Number.NaN;
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
}

function unknownLocation(value: string): LocationDefinition {
  const label = value.trim().replace(/\s+/g, " ");
  return {
    id: normalizedKey(label).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "location",
    label,
    isPreferredSource: true,
  };
}

/**
 * Splits only comma-separated values. A slash remains part of a token so
 * aliases such as "Bengaluru/Bangalore" and "Delhi/NCR" stay intact.
 */
export function normalizeLocationFacet(value: unknown): NormalizedLocation[] {
  if (typeof value !== "string") return [];

  const locations = new Map<string, NormalizedLocation>();
  for (const source of value.split(",").map((token) => token.trim()).filter(Boolean)) {
    const definition = locationDefinitions[normalizedKey(source)] || unknownLocation(source);
    if (!locations.has(definition.id)) {
      locations.set(definition.id, {
        id: definition.id,
        label: definition.label,
        source,
        isPreferredSource: Boolean(definition.isPreferredSource),
      });
    }
  }
  return [...locations.values()];
}

/**
 * Returns only rows that can be linked back to one exact backend facet value.
 * Multi-location aggregates are intentionally excluded: assigning their count
 * to each city would overstate availability when jobs overlap.
 */
export function exactLocationCards(facets: readonly LocationFacetInput[] | unknown): ExactLocationCard[] {
  if (!Array.isArray(facets)) return [];

  const candidates = new Map<string, ExactLocationCard>();
  for (const facet of facets) {
    if (!facet || typeof facet !== "object" || typeof facet.location !== "string") continue;
    const locations = normalizeLocationFacet(facet.location);
    const count = countFrom(facet.count);
    if (facet.location.includes(",") || locations.length !== 1 || count === 0) continue;

    const [location] = locations;
    const candidate = { ...location, count };
    const current = candidates.get(location.id);
    if (!current || (candidate.isPreferredSource && !current.isPreferredSource) ||
      (candidate.isPreferredSource === current.isPreferredSource && candidate.source < current.source)) {
      candidates.set(location.id, candidate);
    }
  }

  return [...candidates.values()].sort((a, b) => b.count - a.count || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
}

export function locationJobsUrl(location: string): string {
  return `/jobs?${new URLSearchParams({ location }).toString()}`;
}

export function matchingJobCountLabel(count: number): string {
  return `${count} matching job${count === 1 ? "" : "s"}`;
}
